import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, KeyRound, Loader2 } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BlockRenderer from "@/components/BlockRenderer";
import { toast } from "sonner";

export default function Giorno() {
  const { n } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [day, setDay] = useState(undefined);
  const [word, setWord] = useState("");
  const [wordContent, setWordContent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/percorso"); return; }
    api.get(`/program/day/${n}`)
      .then((r) => { setDay(r.data); if (r.data.word_unlocked && r.data.word_content) setWordContent(r.data.word_content); })
      .catch(() => setDay(null));
  }, [n, user, loading]);

  const submitWord = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/program/day/${n}/word`, { word });
      if (r.data.correct) {
        setWordContent(r.data.word_content || []);
        setDay((d) => ({ ...d, word_unlocked: true }));
        toast.success("Parola corretta ✦");
      } else {
        toast.error("Parola non corretta");
      }
    } catch { toast.error("Errore"); } finally { setBusy(false); }
  };

  if (day === undefined) return <div className="max-w-3xl mx-auto px-4 py-32"><div className="h-64 bg-[#F3E8E2] animate-pulse rounded-2xl" /></div>;
  if (day === null) return <div className="max-w-3xl mx-auto px-4 py-32 text-center"><h1 className="font-serif text-3xl">Giorno non trovato</h1></div>;

  if (!day.available) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-32 text-center" data-testid="day-locked">
        <div className="w-14 h-14 rounded-full bg-[rgba(198,137,158,0.15)] flex items-center justify-center mx-auto mb-6"><Lock className="w-6 h-6 text-[#C6899E]" /></div>
        <div className="eyebrow mb-2">Giorno {day.day}</div>
        <h1 className="font-serif text-3xl mb-3">Questa stanza è ancora chiusa</h1>
        <p className="text-[#8C7E83] mb-8">Si aprirà automaticamente al momento giusto del tuo percorso.</p>
        <button onClick={() => navigate("/percorso")} className="link-underline text-sm uppercase tracking-[0.15em]">Torna al percorso</button>
      </div>
    );
  }

  return (
    <article data-testid="day-page" className="max-w-3xl mx-auto px-4 sm:px-8 pt-14 sm:pt-20 pb-24">
      <button onClick={() => navigate("/percorso")} className="inline-flex items-center gap-2 text-sm text-[#9C8F94] hover:text-[#C6899E] mb-10" data-testid="day-back">
        <ArrowLeft className="w-4 h-4" /> Il percorso
      </button>

      {day.cover_path && (
        <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-8 border border-[rgba(61,50,56,0.08)]">
          <img src={mediaUrl(day.cover_path)} alt={day.title} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="eyebrow mb-3">Giorno {String(day.day).padStart(2, "0")}</div>
      {day.title && <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight leading-tight mb-10">{day.title}</h1>}

      <BlockRenderer blocks={day.blocks} />

      {/* Parola del giorno */}
      {day.word_required && (
        <div className="mt-16 pt-10 border-t border-[rgba(61,50,56,0.1)]" data-testid="word-of-day">
          {(day.word_unlocked || wordContent) ? (
            <div>
              <div className="eyebrow mb-6 flex items-center gap-2"><KeyRound className="w-4 h-4" /> Parola del giorno · sbloccata</div>
              <BlockRenderer blocks={wordContent || []} />
            </div>
          ) : (
            <div className="rounded-3xl bg-[#ECD8DE] p-8 sm:p-10 text-center">
              <div className="eyebrow mb-3" style={{ color: "#A76B86" }}>Parola del giorno</div>
              <h3 className="font-serif text-2xl text-[#4A2233] mb-6">Inserisci la parola per aprire lo spazio finale</h3>
              <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input value={word} onChange={(e) => setWord(e.target.value)} data-testid="word-input"
                  onKeyDown={(e) => e.key === "Enter" && submitWord()}
                  placeholder="La parola…"
                  className="flex-1 bg-[#FBF5F1] border border-[rgba(74,34,51,0.2)] rounded-full px-5 py-3 text-[#4A2233] text-center focus:border-[#C6899E] focus:outline-none" />
                <button onClick={submitWord} disabled={busy || !word.trim()} data-testid="word-submit"
                  className="inline-flex items-center justify-center gap-2 bg-[#4A2233] text-[#FBF5F1] rounded-full px-7 py-3 text-sm uppercase tracking-[0.15em] hover:opacity-90 disabled:opacity-40">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Apri
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </article>
  );
}
